import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

export default function LoadingScreen({ onComplete }) {
  const [progress, setProgress] = useState(0);
  const done = useRef(false);

  useEffect(() => {
    const start = performance.now();
    const DURATION = 1500;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / DURATION);
      setProgress(Math.round(p * 100));
      if (p < 1) {
        raf = requestAnimationFrame(tick);
      } else if (!done.current) {
        done.current = true;
        setTimeout(onComplete, 350);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [onComplete]);

  return (
    <motion.div
      data-testid="loading-screen"
      className="fixed inset-0 z-[300] flex flex-col items-center justify-center bg-navy scanlines noise"
      exit={{ opacity: 0, transition: { duration: 0.7, ease: 'easeInOut' } }}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {[...Array(14)].map((_, i) => (
          <motion.span
            key={i}
            className="absolute rounded-full bg-electric/50"
            style={{
              width: 3 + (i % 3),
              height: 3 + (i % 3),
              left: `${(i * 71) % 100}%`,
              top: `${(i * 37) % 100}%`
            }}
            animate={{ y: [0, -26, 0], opacity: [0.15, 0.8, 0.15] }}
            transition={{ duration: 3 + (i % 4), repeat: Infinity, delay: i * 0.22 }}
          />
        ))}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(60% 50% at 50% 45%, rgba(22,119,255,0.22) 0%, transparent 70%)'
          }}
        />
        <motion.div
          className="absolute left-0 h-px w-full bg-electric/40"
          animate={{ top: ['8%', '92%', '8%'] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: 'linear' }}
        />
      </div>

      <motion.div
        className="relative"
        initial={{ opacity: 0, scale: 0.82 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <motion.img
          src="/assets/arctoons-logo-opt.webp"
          alt="ARC TOONS logo"
          data-testid="loading-logo"
          className="h-36 w-36 object-contain sm:h-44 sm:w-44"
          animate={{
            y: [0, -8, 0],
            filter: [
              'drop-shadow(0 0 18px rgba(0,200,255,0.35))',
              'drop-shadow(0 0 42px rgba(0,200,255,0.75))',
              'drop-shadow(0 0 18px rgba(0,200,255,0.35))'
            ]
          }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          aria-hidden="true"
          className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/70 to-transparent"
          initial={{ x: '-250%', skewX: '-14deg' }}
          animate={{ x: '380%' }}
          transition={{ delay: 0.7, duration: 0.9, ease: 'easeInOut' }}
          style={{ mixBlendMode: 'overlay' }}
        />
      </motion.div>

      <div className="mt-8 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.35em] text-electric/90">
        <span data-testid="loading-text">ENTERING THE TOONVERSE</span>
        <span className="animate-blink text-gold">...</span>
      </div>

      <div className="mt-5 h-[3px] w-56 overflow-hidden rounded-full bg-electric/15 sm:w-72">
        <div
          className="h-full rounded-full bg-gradient-to-r from-arc via-electric to-gold transition-[width] duration-100 ease-linear"
          style={{ width: `${progress}%` }}
          data-testid="loading-progress"
        />
      </div>
      <div className="mt-3 font-mono text-[10px] tracking-[0.3em] text-snow/50">{progress}%</div>
    </motion.div>
  );
}
